// frontend/src/services/waypointOrderService.js
// Service for managing trip waypoint order (Solution C)

import axios from '../axiosConfig';

const waypointOrderService = {
    /**
     * Get current waypoint order for a trip
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @returns {Promise<Object>} Current order data
     */
    getCurrentOrder: async (entityId, tripId) => {
        const response = await axios.get(
            `/api/trips/${entityId}/${tripId}/waypoint-order`
        );
        return response.data;
    },

    /**
     * Preview a waypoint reorder (dry-run validation)
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {Array<number>} waypointOrder - Array of waypoint IDs in proposed order
     * @returns {Promise<Object>} Preview result with validation
     */
    previewReorder: async (entityId, tripId, waypointOrder) => {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint-order/preview`,
            { waypointOrder }
        );
        return response.data;
    },

    /**
     * Apply a custom waypoint order
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {Array<number>} waypointOrder - Array of waypoint IDs in new order
     * @param {string} reason - Reason for the change
     * @param {number} expectedVersion - Current version for optimistic locking
     * @returns {Promise<Object>} Apply result
     */
    applyCustomOrder: async (entityId, tripId, waypointOrder, reason, expectedVersion) => {
        const response = await axios.put(
            `/api/trips/${entityId}/${tripId}/waypoint-order`,
            { waypointOrder, reason, expectedVersion }
        );
        return response.data?.data ?? response.data;
    },

    /**
     * Reset waypoint order to default route order
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} expectedVersion - Current version for optimistic locking
     * @param {string} reason - Reason for reset
     * @returns {Promise<Object>} Reset result
     */
    resetToDefault: async (entityId, tripId, expectedVersion, reason = null) => {
        const response = await axios.delete(
            `/api/trips/${entityId}/${tripId}/waypoint-order`,
            { data: { expectedVersion, reason } }
        );
        return response.data?.data ?? response.data;
    },

    /**
     * Driver acknowledges a reorder
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} expectedVersion - Current version for optimistic locking
     * @returns {Promise<Object>} Acknowledgment result
     */
    acknowledgeReorder: async (entityId, tripId, expectedVersion) => {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint-order/acknowledge`,
            { expectedVersion }
        );
        return response.data;
    },

    /**
     * Driver rejects a reorder
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number} expectedVersion - Current version for optimistic locking
     * @param {string} reason - Reason for rejection
     * @returns {Promise<Object>} Rejection result
     */
    rejectReorder: async (entityId, tripId, expectedVersion, reason) => {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint-order/reject`,
            { expectedVersion, reason }
        );
        return response.data;
    },

    /**
     * Lock or unlock waypoint order for a trip
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {boolean} locked - Whether to lock or unlock
     * @param {string} reason - Reason for locking/unlocking
     * @returns {Promise<Object>} Lock result
     */
    setOrderLock: async (entityId, tripId, locked, reason = null) => {
        const response = await axios.put(
            `/api/trips/${entityId}/${tripId}/waypoint-order/lock`,
            { locked, reason }
        );
        return response.data;
    },

    /**
     * Get available vehicles for the trip's entity
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @returns {Promise<Object>} Vehicles and current vehicle ID
     */
    getAvailableVehicles: async (entityId, tripId) => {
        const response = await axios.get(
            `/api/trips/${entityId}/${tripId}/waypoint-order/vehicles`
        );
        return response.data;
    },

    /**
     * Change vehicle assignment for a trip
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @param {number|null} vehicleId - New vehicle ID (null to unassign)
     * @returns {Promise<Object>} Updated order data with recalculated capacity flow
     */
    changeVehicle: async (entityId, tripId, vehicleId) => {
        const response = await axios.put(
            `/api/trips/${entityId}/${tripId}/waypoint-order/vehicle`,
            { vehicleId }
        );
        return response.data;
    },

    /**
     * Request on-demand optimization suggestion for existing bookings
     * @param {number} entityId - Entity ID
     * @param {number} tripId - Trip ID
     * @returns {Promise<Object>} Optimization result with suggested order or alreadyOptimal flag
     */
    suggestOptimalOrder: async (entityId, tripId) => {
        const response = await axios.post(
            `/api/trips/${entityId}/${tripId}/waypoint-order/suggest-optimal`
        );
        return response.data;
    }
};

export default waypointOrderService;
